import React, { useEffect, useState, useRef } from 'react';
//@ts-ignore
import * as styles from './TestimonialScroll.module.css';
import ArrowLgButton from '../Global/ArrowLgButton';
import {
    KontentStringValue,
    KontentMedia,
    CTALocation,
    KontentRadioMultiChoice,
    KontentRichText,
} from '../../../types/KontentProps';
import CtaLink from '../Global/CtaLink';
import RichText from '../Global/RichText';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import { getMediaData, getMultiChoice } from '../Global/DataUtils';
import { graphql } from 'gatsby';

type WindowAction = 'New Window' | 'Same Window';
type Scroll = {
    id?: number;
    elements: {
        cta_configuration__cta_label: KontentStringValue;
        cta_configuration__cta_location: CTALocation;
        cta_configuration__cta_location_anchor_point: KontentStringValue;
        cta_configuration__cta_window_action: KontentRadioMultiChoice<WindowAction>;
        image: KontentMedia;

        partner_name: KontentStringValue;

        partner_description: KontentStringValue;
        testimonial_quote: KontentRichText;
    };
};

type galleryWidth = number;
type left = number;
type width = number;
type cardWidth = number;
type showWidth = number;
type dotsToShow = number;

interface Props {
    layout: {
        value: [
            {
                name: string;
            }
        ];
    };
    id?: string;
    testimonials: {
        value: Scroll[];
    };
}

const TestimonialScroll: React.FC<Props> = ({ testimonials, layout }) => {
    const [currentScroll, setCurrentScroll] = useState(0);
    const [currentPosition, setCurrentPosition] = useState(0);
    const [width, setWidth] = useState(0);
    const [left, setLeft] = useState(0);
    const galleryWrap: any = useRef(null);
    const cardWidth = width > 360 ? 300 : 278;
    const galleryWidth = testimonials.value.length * cardWidth;
    const showWidth = width > 360 ? Math.floor((width - 50) / cardWidth) : 1;
    const dotsToShow =
        width > 360
            ? Math.ceil(galleryWidth / cardWidth / showWidth)
            : testimonials.value.length;

    let clickNext = setGalleryPosition(
        galleryWidth,
        width,
        left,
        showWidth,
        cardWidth
    );
    let newGalleryWidth = setNewGalleryWidth(galleryWidth, width, cardWidth);

    const handleClickNext = () => {
        setCurrentScroll(currentScroll + 1);

        setCurrentPosition(currentPosition + 1);
        setLeft(left - cardWidth * showWidth);
    };

    const handleClickPrev = () => {
        setCurrentScroll(currentScroll - 1);

        setCurrentPosition(currentPosition - 1);
        setLeft(left + cardWidth * showWidth);
    };

    const renderPrevButton = () => (
        <ArrowLgButton
            strokeColor="#f06623"
            direction="left"
            handleClick={handleClickPrev}
            className={styles.prevArrowButton}
        />
    );

    const renderNextButton = () => (
        <ArrowLgButton
            strokeColor="#f06623"
            direction="right"
            handleClick={handleClickNext}
            className={styles.nextArrowButton}
        />
    );

    const renderDots = (dotsToShow: number, currentPosition: number) => {
        let dots = [];

        for (let i = 0; i < dotsToShow; i++) {
            dots.push(
                <span
                    key={i}
                    style={currentPosition == i ? { background: '#ccc' } : {}}
                ></span>
            );
        }

        return dots;
    };

    const renderScrollCard = (scrollCard: Scroll, cardIdx: number) => {
        const quote = RichText(scrollCard.elements.testimonial_quote);
        return (
            <div className={styles.scrollCard} key={cardIdx}>
                {scrollCard.elements.image.value?.length > 0 && (
                    <div>
                        <img
                            className={styles.img}
                            src={getMediaData(scrollCard.elements.image)?.url}
                        />
                    </div>
                )}

                <div className={styles.paragraph}>{quote}</div>

                <div className={styles.name}>
                    {scrollCard.elements.partner_name.value.substring(0, 25)}
                </div>
                <div className={styles.subtitle}>
                    {scrollCard.elements.partner_description.value}
                </div>

                {scrollCard.elements.cta_configuration__cta_label.value && (
                    <div>
                        <div className={styles.label}>
                            <CtaLink
                                href={BuildLinkHref(
                                    scrollCard.elements
                                        .cta_configuration__cta_location
                                )}
                                target={
                                    getMultiChoice(
                                        scrollCard.elements
                                            .cta_configuration__cta_window_action
                                    ) === 'new_window'
                                        ? '_blank'
                                        : undefined
                                }
                            >
                                {
                                    scrollCard.elements
                                        .cta_configuration__cta_label.value
                                }
                            </CtaLink>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    useEffect(() => {
        const resizeListener = () => {
            const newWidth = galleryWrap.current?.getBoundingClientRect().width;

            if (newWidth !== width) {
                setWidth(newWidth);
                setCurrentScroll(0);
                setCurrentPosition(0);
                setLeft(0);
            }
        };

        resizeListener();
        window.addEventListener('resize', resizeListener);

        return () => {
            window.removeEventListener('resize', resizeListener);
        };
    }, [width]);

    return (
        <>
            {layout?.value[0]?.name === 'Carousel' ? (
                <div ref={galleryWrap} className={styles.wrapper}>
                    <div
                        id={styles.gallery}
                        className={styles.gallery}
                        style={newGalleryWidth}
                    >
                        {currentScroll > 0 ? renderPrevButton() : null}
                        <div
                            className={styles.gallerytestimonials}
                            style={clickNext}
                        >
                            {testimonials?.value.map((Scroll, cardIdx) => {
                                return renderScrollCard(Scroll, cardIdx);
                            })}
                        </div>
                        {galleryWidth + 50 > width &&
                        currentPosition + 1 < dotsToShow
                            ? renderNextButton()
                            : null}
                    </div>

                    {dotsToShow > 1 && (
                        <div className={styles.dots}>
                            {renderDots(dotsToShow, currentPosition)}
                        </div>
                    )}
                </div>
            ) : (
                <div ref={galleryWrap} className={styles.container}>
                    {testimonials?.value.map((Scroll, cardIdx) => {
                        return renderScrollCard(Scroll, cardIdx);
                    })}
                </div>
            )}
        </>
    );
};

export default TestimonialScroll;

function setGalleryPosition(
    galleryWidth: galleryWidth,
    width: width,
    marginLeft: left,
    showWidth: showWidth,
    cardWidth: cardWidth
) {
    let newWidth;

    if (galleryWidth < width) {
        if (width / showWidth > cardWidth) {
            newWidth = galleryWidth + cardWidth;
        } else {
            newWidth = width;
        }
    } else {
        newWidth = galleryWidth;
    }
    const galleryPosition: React.CSSProperties = {
        width: newWidth,
        marginLeft: marginLeft,
        transition: 'margin .5s ease-in-out',
    };

    return galleryPosition;
}

function setNewGalleryWidth(
    galleryWidth: galleryWidth,
    width: width,
    cardWidth: cardWidth
) {
    let showWidth = 1;
    let newGalleryWidth = showWidth * cardWidth;

    if (width > 320) {
        showWidth = Math.floor((width - 50) / cardWidth);
        newGalleryWidth = showWidth * cardWidth;
    }

    if (newGalleryWidth > galleryWidth) {
        newGalleryWidth = galleryWidth;
    }

    const galleryInsideWidth: React.CSSProperties = {
        width: newGalleryWidth,
    };

    return galleryInsideWidth;
}

export const fragmentPartnerTestimonials = graphql`
    fragment PartnerTestimonials on kontent_item_component___partner_testimonials {
        elements {
            layout {
                value {
                    name
                }
            }
            testimonials {
                value {
                    ... on kontent_item_content_item___partner_testimonial_card {
                        id
                        elements {
                            cta_configuration__cta_label {
                                value
                            }
                            cta_configuration__cta_location {
                                value {
                                    ... on kontent_item_content_item___third_party_link {
                                        id
                                        elements {
                                            third_party_url {
                                                value
                                            }
                                        }
                                    }
                                }
                            }
                            cta_configuration__cta_location_anchor_point {
                                value
                            }
                            cta_configuration__cta_window_action {
                                value {
                                    name
                                }
                            }
                            image {
                                value {
                                    ... on kontent_item_media___image {
                                        id
                                        elements {
                                            file {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            partner_description {
                                value
                            }
                            partner_name {
                                value
                            }
                            testimonial_quote {
                                value
                            }
                        }
                    }
                }
            }
        }
    }
`;
